import type { ChainMetadata } from '@polaris/core/models'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronRight } from 'lucide-react'
import { useQueryStates } from 'nuqs'
import { type ReactNode, useMemo } from 'react'
import { apiClient } from '@/src/services/client'
import {
  fromSearchParamsParsers,
  toSearchParamsParsers,
} from '@/src/stores/params'
import {
  useNetworkSelection,
  useTokenSelection,
} from '@/src/stores/token-selection/actions'
import { ChainImg } from '../../ui/chain-img'
import { ListRowButton, ListRowSide, ListRowSideName } from '../../ui/list-row'
import { NoSearchResults } from '../../ui/no-search-results'
import { Skeleton } from '../../ui/skeleton'
import { TooltipProvider } from '../../ui/tooltip'
import { FilterButtonImage } from './filter-button'
import { NetworkRow } from './row'
import type { ChainToken } from './types'

interface NetworkRowsProps {
  filteredChains: ChainToken[]
  isLoading: boolean
  query: string
  onSelectNetwork: (chainToken: ChainToken | null) => void
  showAnyNetwork?: boolean
  header?: string | ReactNode
  shouldUseDirectionalChain?: boolean
  showNetworksWithOtherTokens?: boolean
  networksWithOtherTokens?: ChainToken[]
  onSelectOtherNetwork?: (props: ChainMetadata) => void
  showTooltips?: boolean
}

export const NetworkRows = ({
  filteredChains,
  isLoading,
  query,
  onSelectNetwork,
  showAnyNetwork = true,
  header,
  shouldUseDirectionalChain = false,
  showNetworksWithOtherTokens = false,
  networksWithOtherTokens = [],
  onSelectOtherNetwork,
  showTooltips = true,
}: NetworkRowsProps) => {
  const { filteredNetwork } = useTokenSelection()
  const { direction } = useNetworkSelection()

  const { data: enabledChains } = useQuery({
    queryKey: ['enabled-chains'],
    queryFn: async () => {
      return (await apiClient.get('ui-chains/enabled').json()) as {
        enabled_chains: string[]
      }
    },
    select: (data) => data.enabled_chains,
  })

  const [{ fromChain, fromToken, toToken, toChain }] = useQueryStates({
    ...toSearchParamsParsers,
    ...fromSearchParamsParsers,
  })

  const directionalChain = useMemo(() => {
    if (shouldUseDirectionalChain) {
      return direction === 'from' ? fromChain : toChain
    }

    return filteredNetwork?.chain_id
  }, [
    shouldUseDirectionalChain,
    direction,
    fromChain,
    toChain,
    filteredNetwork,
  ])

  const directionalDenom = useMemo(() => {
    if (!shouldUseDirectionalChain) return

    return direction === 'from' ? fromToken : toToken
  }, [direction, fromToken, shouldUseDirectionalChain, toToken])

  if (isLoading) {
    return <NetworkSelectionSkeleton />
  }

  if (filteredChains.length === 0 && networksWithOtherTokens.length === 0) {
    return (
      <div className='flex items-center justify-center'>
        <NoSearchResults failedQuery={query} />
      </div>
    )
  }

  return (
    <div className='flex flex-col px-2 pb-4 overflow-auto no-scrollbar'>
      {filteredChains.length > 0 && (
        <>
          <div className='flex w-full px-4 pt-4 pb-2'>
            {typeof header === 'string' ? (
              <span className='text-secondary text-sm'>{header}</span>
            ) : (
              header
            )}
          </div>
          {showAnyNetwork && (
            <ListRowButton
              className='py-2'
              onClick={() => onSelectNetwork(null)}
            >
              <ListRowSide className='flex-row items-center gap-3'>
                <FilterButtonImage size='sm' />
                <ListRowSideName>Any network</ListRowSideName>
              </ListRowSide>
              {!filteredNetwork && <Check className='size-6 text-link' />}
            </ListRowButton>
          )}
          <TooltipProvider>
            {filteredChains.map(
              ({
                chain_id,
                chain_img,
                chain_name,
                chain_type,
                display_identifier,
                denom,
              }) => (
                <NetworkRow
                  key={`${chain_id}-${denom}`}
                  showTooltips={showTooltips}
                  directionalChain={directionalChain}
                  directionalDenom={directionalDenom}
                  onSelectNetwork={onSelectNetwork}
                  chain_id={chain_id}
                  chain_img={chain_img}
                  chain_name={chain_name}
                  chain_type={chain_type}
                  display_identifier={display_identifier}
                  denom={denom}
                  isDisabled={!enabledChains?.includes(chain_id)}
                />
              ),
            )}
          </TooltipProvider>
        </>
      )}
      {showNetworksWithOtherTokens && (
        <>
          <div className='flex w-full px-4 pt-4 pb-2'>
            <span className='text-secondary text-sm'>
              Networks with other tokens
            </span>
          </div>
          {networksWithOtherTokens.map(
            ({ chain_id, chain_img, chain_name, chain_type }) => {
              return (
                <ListRowButton
                  key={`${chain_id}-${chain_type}-other-network`}
                  onClick={() => {
                    onSelectOtherNetwork?.({
                      chain_id,
                      chain_img,
                      chain_name,
                      chain_type,
                    })
                  }}
                  className='group py-2'
                >
                  <ListRowSide className='flex-row items-center gap-3'>
                    <ChainImg
                      chainId={chain_id}
                      chainImg={chain_img}
                      chainName={chain_name}
                      width={20}
                      height={20}
                      containerClassName='size-8'
                    />
                    <ListRowSideName className='text-secondary group-hover:text-primary group-focus:text-primary transition-colors'>
                      {chain_name}
                    </ListRowSideName>
                  </ListRowSide>
                  <ListRowSide>
                    <ChevronRight className='size-6 text-secondary group-hover:text-primary group-focus:text-primary transition-colors' />
                  </ListRowSide>
                </ListRowButton>
              )
            },
          )}
        </>
      )}
    </div>
  )
}

const NetworkSelectionSkeleton = () => {
  return (
    <div className='flex flex-col px-2'>
      {Array(4)
        .fill(null)
        .map((_, index) => (
          <div key={index} className='flex items-center gap-3 px-4 py-2'>
            <Skeleton className='size-8 rounded-full' />
            <Skeleton className='w-52 h-6' />
          </div>
        ))}
    </div>
  )
}
